// components/list/ConfigurationList.tsx

import React from 'react';
import {
    Table,
    Button,
    Space,
    Tag,
    Tooltip,
    Dropdown,
    Menu,
    Typography
} from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    MoreOutlined,
    EyeOutlined,
    DownloadOutlined,
    CopyOutlined
} from '@ant-design/icons';
import { ActionFlowConfigurationDTO, ConfigurationStatus } from '../../types/configuration';

const { Text } = Typography;

interface ConfigurationListProps {
    configurations: ActionFlowConfigurationDTO[];
    isLoading: boolean;
    onEdit: (id: string) => void;
    onDelete: (id: string, name: string) => void;
    onView: (id: string) => void;
    onDuplicate: (configuration: ActionFlowConfigurationDTO) => void;
    onDownload: (configuration: ActionFlowConfigurationDTO) => void;
}

const ConfigurationList: React.FC<ConfigurationListProps> = ({
                                                                 configurations,
                                                                 isLoading,
                                                                 onEdit,
                                                                 onDelete,
                                                                 onView,
                                                                 onDuplicate,
                                                                 onDownload
                                                             }) => {
    // Get status tag based on configuration properties
    const getStatusTag = (config: ActionFlowConfigurationDTO) => {
        // This is a placeholder - in a real application, you'd have a status field
        // or derive it from configuration properties
        const subscriptionCount = Math.floor(Math.random() * 3); // Mock subscription count

        if (subscriptionCount > 0) {
            return (
                <Tooltip title={`Used in ${subscriptionCount} workflow(s)`}>
                    <Tag color="green">ACTIVE</Tag>
                </Tooltip>
            );
        }

        return <Tag color="blue">DRAFT</Tag>;
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string) => <Text strong>{text}</Text>,
            sorter: (a: ActionFlowConfigurationDTO, b: ActionFlowConfigurationDTO) =>
                a.name.localeCompare(b.name)
        },
        {
            title: 'Version',
            dataIndex: 'version',
            key: 'version',
            width: '10%',
            render: (version: string) => <Tag>{version}</Tag>,
            sorter: (a: ActionFlowConfigurationDTO, b: ActionFlowConfigurationDTO) =>
                a.version.localeCompare(b.version)
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: {
                showTitle: false,
            },
            render: (description: string) => (
                <Tooltip placement="topLeft" title={description}>
                    <div className="max-w-md truncate">{description}</div>
                </Tooltip>
            )
        },
        {
            title: 'Status',
            key: 'status',
            width: '10%',
            render: (_: any, record: ActionFlowConfigurationDTO) => getStatusTag(record)
        },
        {
            title: 'Actions',
            key: 'actions',
            width: '15%',
            render: (_: any, record: ActionFlowConfigurationDTO) => (
                <Space size="small">
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => onEdit(record.id)}
                        />
                    </Tooltip>

                    <Tooltip title="View">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => onView(record.id)}
                        />
                    </Tooltip>

                    <Dropdown menu={{
                        items: [
                            {
                                key: 'duplicate',
                                icon: <CopyOutlined />,
                                label: 'Duplicate',
                                onClick: () => onDuplicate(record)
                            },
                            {
                                key: 'download',
                                icon: <DownloadOutlined />,
                                label: 'Download',
                                onClick: () => onDownload(record)
                            },
                            {
                                type: 'divider'
                            },
                            {
                                key: 'delete',
                                icon: <DeleteOutlined />,
                                label: 'Delete',
                                danger: true,
                                onClick: () => onDelete(record.id, record.name)
                            }
                        ]
                    }} trigger={['click']}>
                        <Button
                            type="text"
                            icon={<MoreOutlined />}
                        />
                    </Dropdown>
                </Space>
            ),
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={configurations}
            rowKey="id"
            loading={isLoading}
            pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} configurations`
            }}
            onChange={(pagination, filters, sorter) => {
                // You can implement sorting/filtering logic here
                console.log('Table params changed:', pagination, filters, sorter);
            }}
            rowClassName="hover:bg-gray-50 cursor-pointer"
            onRow={(record) => ({
                onClick: () => onView(record.id)
            })}
        />
    );
};

export default ConfigurationList;